'use strict'

import _ from 'underscore'
import PropTypes from 'prop-types'
import React from 'react'

import Chart from './timeline/chart'
import Span from './timeline/span'
import Event from './timeline/event'

import SpanService from './spanService'
import TimelineTraceDataTransformer from './timeline/TimelineTraceDataTransformer'
import { memoryConverter } from '../helper/memoryConverter'
import { TraceDetailsLayerReport } from './tracedetails/TraceDetailsLayerReport'
import { formatMilliseconds } from '../helper/milliseconds'

const getTraceServerName = trace => {
    return trace.server ? trace.server.name : 'unknown server'
}

class Timeline extends React.Component {
    constructor(props) {
        super(props)
        this.chart = new Chart()
        this.spanService = new SpanService()
        this.traceService = new TimelineTraceDataTransformer()
        this.traces = []
        this.spans = []
        this.spansHidden = false
        this.spansHiddenThreshold = 0
        this.state = {
            toggleMemory: this.props.params.displayMemory == 1,
            displayChildTraces: true,
            chartToTop: 170, // 170px is the default position of the chart to the top of the page
        }
    }

    _plotValuesCallback(span, event) {
        if (this.state.toggleMemory) {
            return event ? { s: span.ms || 0, t: 0 } : { s: span.ms || 0, t: span.mc || 0 }
        }

        return event ? { s: event.cs, t: event.wt } : { s: span.cs, t: span.wt }
    }

    _tickFormatCallback(d) {
        if (this.state.toggleMemory) {
            return memoryConverter(d, 1000, 4)
        }

        return formatMilliseconds(d / 1000, false, 4)
    }

    onMemoryClick = () => {
        this.setState({ toggleMemory: true })
    }

    onTimeClick = () => {
        this.setState({ toggleMemory: false })
    }

    componentDidMount() {
        let chartElement = this.getChartElement()
        let parentNode = jQuery(chartElement).parent()
        this.chart.create(chartElement, parentNode.width(), this._plotValuesCallback.bind(this), this._tickFormatCallback.bind(this))

        this.setState({ chartToTop: parentNode.offset().top })

        jQuery(chartElement).on('spanSelected', (event, span) => {
            this.props.router.replace(
                'XhprofProfilerBundle.Profile.quick',
                { ...this.props.params, span: span.id, displayMemory: this.state.toggleMemory },
                true,
            )
        })

        jQuery(chartElement).on(
            'eventSelected',
            function(event, span, spanEvent) {
                let params = {
                    ...this.props.params,
                    ...{ span: span.id, e: spanEvent.cs },
                }
                this.props.router.replace('XhprofProfilerBundle.Profile.quick', params, true)
            }.bind(this),
        )

        // jQuery(window).on('scroll', function () {
        //     // Guard for the case where the node is already unmounted but the
        //     // event wasn't already detached.
        //     if (!jQuery('.timeline-chart').length ||
        //         !jQuery('.timeline-chart').is(':visible')) {
        //         return
        //     }
        //
        //     jQuery('.timeline-info').offset({
        //         top: Math.max(
        //             jQuery('body').scrollTop() + 5,
        //             jQuery('html').scrollTop() + 5,
        //             (jQuery('.timeline-chart').offset() || {}).top
        //         ),
        //     })
        // })

        this.updateChart()
    }

    componentDidUpdate(prevProps, prevState) {
        const traceChanged = prevProps.traces !== this.props.traces
        const displayModeChanged = prevState.toggleMemory !== this.state.toggleMemory
        const childTraceModeChanged = prevState.displayChildTraces !== this.state.displayChildTraces

        this.updateChart(traceChanged || displayModeChanged || childTraceModeChanged)
    }

    prepareTraceData() {
        const includeChildTraces = this.state.displayChildTraces && !this.state.toggleMemory

        const rootTrace = this.props.traces[0]
        const childTraces = includeChildTraces ? this.props.traces.slice(1) : []
        let traces = [rootTrace, ...childTraces]

        // We need to manipulate some data. To not change props in place we need to clone the relevant parts first.
        traces = this.traceService.cloneTraceSpanAndEventData(traces)

        traces = this.traceService.alignChildTracesToParentSpans(traces)

        // Realigning traces might have shifted traces. Reorder them for a cleaner visualization.
        this.traceService.sortTracesByStartTime(traces)

        const filterInfo = this.traceService.filterFastSpansFromTraces(traces)

        this.spansHidden = filterInfo.hidden
        this.spansHiddenThreshold = filterInfo.hiddenThreshold

        for (let trace of traces) {
            trace.spans = this.spanService.sort(trace.spans)
        }

        this.traces = traces
        this.spans = traces.reduce((spans, trace) => [...spans, ...trace.spans], [])
    }

    componentWillUnmount() {
        jQuery(window).off('scroll')
        this.chart.destroy()
    }

    getChartElement() {
        return this.svg
    }

    getSelectedSpan() {
        if (this.props.params && this.props.params.span) {
            return this.props.params.span
        }

        if (this.props.params && this.props.params.call && this.props.traces && this.spans) {
            let spans = _.filter(
                this.spans,
                function(span) {
                    return span.annotations && 'fn' in span.annotations && span.annotations.fn === this.props.params.call
                }.bind(this),
            )

            if (spans[0]) {
                return spans[0].id
            }
        }

        if (this.props.traces && this.spans && this.spans[0]) {
            return this.spans[0].id
        }

        return null
    }

    getTraceBounds(trace) {
        const tickIds = Object.keys(trace.ticks)
        const rootSpan = trace.spans[0]

        return {
            traceId: trace.id,
            service: trace.service,
            environment: trace.environment,
            measureDate: trace.measureDate,
            serverName: getTraceServerName(trace),
            xStart: rootSpan.cs,
            xEnd: rootSpan.cs + rootSpan.wt,
            yStart: tickIds[0],
            yEnd: tickIds[tickIds.length - 1],
        }
    }

    getTraceGlobalEventsWithOffset(trace) {
        const tickIds = Object.keys(trace.ticks)

        return trace.globalEvents.map(globalEvent => ({
            yStart: tickIds[0],
            ...globalEvent,
        }))
    }

    updateChart(refreshScales = false) {
        const allTicks = this.props.traces.reduce((ticks, childTrace) => ({ ...ticks, ...childTrace.ticks }), {})
        let ticks = this.spanService.filterTicks(allTicks, this.spans)

        const globalEvents = this.state.toggleMemory
            ? []
            : this.traces.reduce((globalEvents, trace) => [...globalEvents, ...this.getTraceGlobalEventsWithOffset(trace)], [])

        this.chart.update(
            this.spans,
            ticks,
            globalEvents,
            this.traces.slice(1).map(trace => this.getTraceBounds(trace)),
            this.props.categories,
            this.getSelectedSpan(),
            this.props.params.e || null,
            refreshScales,
        )
    }

    render() {
        this.prepareTraceData()

        const rootTrace = this.traces[0]
        let categories = _.pick(this.props.categories, _.unique(_.pluck(this.spans, 'cat')))

        let selectedSpanId = this.getSelectedSpan()
        let selectedSpan =
            _.find(this.spans, function(span) {
                return span.id === selectedSpanId
            }) || null
        let selectedSpanIsRoot = selectedSpan && selectedSpan.id === rootTrace.spans[0].id
        let selectedTrace = _.find(this.traces, trace => {
            for (let span of trace.spans) {
                if (span.id === selectedSpanId) {
                    return true
                }
            }

            return false
        })
        let truncateSpan =
            _.find(this.spans, function(span) {
                return span && span.annotations && 'trunc_spans' in span.annotations
            }) || null

        return (
            <div className="row">
                <div className="col-lg-8 col-md-12">
                    <div className="timeline-chart box">
                        <div className="box-body">
                            {truncateSpan ? (
                                <div className="row">
                                    <div className="alert alert-warning">
                                        <p>
                                            This trace is very large and Tideways has truncated {truncateSpan.annotations['trunc_spans']}{' '}
                                            spans that happened after the threshold of 1500 spans per trace.
                                        </p>
                                    </div>
                                </div>
                            ) : null}
                            {rootTrace.memoryLimit ? (
                                <div className="row">
                                    <div className="alert alert-warning">
                                        <p>
                                            This trace contains a large amount of individual spans and Tideways was not able to process and
                                            display all of them.
                                        </p>
                                    </div>
                                </div>
                            ) : null}
                            {this.spansHidden ? (
                                <div className="row">
                                    <p>
                                        Hidden {this.spansHidden} spans slower than {(this.spansHiddenThreshold / 1000).toFixed(0)} ms. See
                                        all spans in Summary.
                                    </p>
                                </div>
                            ) : null}
                            <div className="row">
                                <div className="col-lg-5">
                                    {!this.props.hasMemory ? null : (
                                        <div style={{ display: 'inline-block' }}>
                                            <div className="btn-group">
                                                <button
                                                    type="button"
                                                    className={
                                                        this.state.toggleMemory ? 'btn btn-xs btn-default' : 'btn btn-xs btn-default active'
                                                    }
                                                    onClick={this.onTimeClick}
                                                >
                                                    Time
                                                </button>
                                                <button
                                                    type="button"
                                                    className={
                                                        this.state.toggleMemory ? 'btn btn-xs btn-default active' : 'btn btn-xs btn-default'
                                                    }
                                                    onClick={this.onMemoryClick}
                                                >
                                                    Memory
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {this.props.traces && this.props.traces.length > 1 && !this.state.toggleMemory ? (
                                        <div className="checkbox checkbox-primary tw-ml-8" style={{ display: 'inline-block' }}>
                                            <input
                                                type="checkbox"
                                                name="displayChildTraces"
                                                id="timeline_displayChildTraces"
                                                style={{ top: '20px' }}
                                                checked={this.state.displayChildTraces}
                                                onChange={ev => this.setState({ displayChildTraces: ev.target.checked })}
                                            />
                                            <label htmlFor="timeline_displayChildTraces">Display child traces</label>
                                        </div>
                                    ) : null}
                                </div>
                                <div className="col-lg-7 legend">
                                    <ul className="categories-list list-unstyled list-inline">
                                        {_.map(categories, function(category, key) {
                                            return (
                                                <li key={key}>
                                                    <i className={'fa fa-circle category-' + category.className} /> {category.label}
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            </div>
                            <div
                                className="timeline-chart__container"
                                style={{
                                    maxHeight: `calc(100vh - ${Math.max(0, Math.round(this.state.chartToTop))}px)`,
                                }}
                            >
                                <svg ref={svg => (this.svg = svg)} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4 col-md-12">
                    <div className="timeline-info">
                        {this.spans && selectedSpan ? (
                            <Span
                                wt={this.spans[0].wt}
                                mc={this.spans[0].mc}
                                span={selectedSpan}
                                traceInfo={{
                                    traceId: selectedTrace.id,
                                    serverName: getTraceServerName(selectedTrace),
                                    service: selectedTrace.service,
                                    isChildTrace: selectedTrace.id !== this.traces[0].id,
                                    environment: selectedTrace.environment,
                                    measureDate: selectedTrace.measureDate,
                                }}
                                parents={this.spanService.getParents(this.spans, selectedSpan)}
                                categories={this.props.categories}
                                hasCallgraph={this.props.hasCallgraph}
                                route={'XhprofProfilerBundle.Profile.quick'}
                                showSettingsLink={!this.props.publicVersion}
                                sectionBelowTitle={
                                    selectedSpanIsRoot && this.props.layerReport ? (
                                        <TraceDetailsLayerReport layerReport={this.props.layerReport} wt={this.spans[0].wt} />
                                    ) : (
                                        undefined
                                    )
                                }
                                bottleneckRoute={
                                    this.props.publicVersion
                                        ? 'XhprofCoreBundle.Public.sharedTrace.bottlenecks'
                                        : 'XhprofProfilerBundle.Profile.bottlenecksReport'
                                }
                                operationId={this.props.operationId}
                                isRoot={selectedSpanIsRoot}
                            >
                                {this.spans && selectedSpan && this.props.params.e ? (
                                    <>
                                        <hr className="tw-m-0" />
                                        <Event
                                            span={selectedSpan}
                                            event={_.findWhere(selectedSpan.e, { cs: +this.props.params.e })}
                                            categories={this.props.categories}
                                        />
                                    </>
                                ) : null}
                            </Span>
                        ) : null}
                    </div>
                </div>
            </div>
        )
    }
}

Timeline.propTypes = {
    categories: PropTypes.object,
    traces: PropTypes.arrayOf(PropTypes.object),
    hasCallgraph: PropTypes.bool,
    router: PropTypes.object.isRequired,
    params: PropTypes.object,
    publicVersion: PropTypes.bool,
    operationId: PropTypes.number,
    layerReport: PropTypes.object,
}

export default Timeline
